import React from 'react';
import { toast } from 'sonner';
import { ChatCenteredDotsIcon, ChecksIcon, XIcon } from '@phosphor-icons/react';
import { EmptyState } from '@/components/ui/empty-state';
import { IconButton } from '@/components/ui/icon-button';
import { PanelClose } from '@/components/ui/panel';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import NotificationCard from './NotificationCard';
import { Notification } from './types/domain.types';

interface NotificationPanelProps {
  notifications: Notification[];
  markAsRead: (id: number) => Promise<void> | void;
  markAllAsRead: () => Promise<void> | void;
}

/** The notifications panel's content, inside a PanelContent its caller opens from its notifications button. */
const NotificationPanel: React.FC<NotificationPanelProps> = ({ notifications, markAsRead, markAllAsRead }) => {
  const hasUnread = notifications.some(n => !n.read);

  const reportFailure = (error: unknown) => {
    console.error('Error marking notifications read:', error);
    toast.error("Couldn't mark as read. Please try again.");
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) Promise.resolve(markAsRead(notification.id)).catch(reportFailure);
  };

  // Production's panel: a plain header with its actions, then the cards
  return (
    <div className="flex max-h-100 min-h-0 flex-col p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-lureo-14 font-medium text-lureo-ink">Notifications</span>
        <div className="flex items-center gap-1">
          {hasUnread && (
            <Tooltip>
              <TooltipTrigger asChild>
                <IconButton label="Mark all as read" onClick={() => Promise.resolve(markAllAsRead()).catch(reportFailure)}>
                  <ChecksIcon className="h-4 w-4" />
                </IconButton>
              </TooltipTrigger>
              <TooltipContent sideOffset={4}>Mark all as read</TooltipContent>
            </Tooltip>
          )}
          <PanelClose asChild>
            <IconButton label="Close">
              <XIcon className="h-4 w-4" />
            </IconButton>
          </PanelClose>
        </div>
      </div>
      <div className="flex min-h-0 flex-col gap-1.5 overflow-y-auto">
        {notifications.length === 0 ? (
          <EmptyState icon={<ChatCenteredDotsIcon />}>No notifications</EmptyState>
        ) : (
          notifications.map(notification => <NotificationCard key={notification.id} notification={notification} onClick={handleNotificationClick} />)
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
